import React, { useMemo, useCallback } from "react";
import { twMerge } from "tailwind-merge";

import AngleLeftIcon from "@svg/angle-left";
import { toFarsiNumber } from "@utils/digits";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageItemsToShow: number;
}

const baseItemClass =
  "px-2 min-w-10 h-10 flex items-center justify-center rounded-md bg-white text-[#71717A] border border-[#E4E4E7] text-base font-normal leading-[38px]";

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  pageItemsToShow,
}) => {
  const pages = useMemo(() => {
    if (totalPages <= pageItemsToShow) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const half = Math.floor(pageItemsToShow / 2);

    let start = currentPage - half;
    let end = currentPage + half;

    if (start < 1) {
      start = 1;
      end = pageItemsToShow;
    }

    if (end > totalPages) {
      end = totalPages;
      start = totalPages - pageItemsToShow + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentPage, totalPages, pageItemsToShow]);

  const handleChange = useCallback(
    (page: number) => {
      if (page !== currentPage) {
        onPageChange(page);
      }
    },
    [currentPage, onPageChange]
  );

  const showStartEllipsis = pages[0] > 1;
  const showEndEllipsis = pages[pages.length - 1] < totalPages;

  return (
    <div className="flex items-center justify-center gap-1.5">
      <button
        className="w-10 h-10 flex items-center justify-center rounded-md bg-white text-[#71717A] disabled:text-[#D4D4D8]"
        onClick={() => handleChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <AngleLeftIcon className="-rotate-180" />
      </button>

      {showStartEllipsis && (
        <>
          <button className={baseItemClass} onClick={() => handleChange(1)}>
            {toFarsiNumber(1)}
          </button>
          <span className={baseItemClass}>...</span>
        </>
      )}

      {pages.map((page) => (
        <button
          key={page}
          className={twMerge(
            baseItemClass,
            page === currentPage &&
              "bg-fdPrimary-9 border-transparent text-white cursor-default"
          )}
          onClick={() => handleChange(page)}
        >
          {toFarsiNumber(page)}
        </button>
      ))}

      {showEndEllipsis && (
        <>
          <span className={baseItemClass}>...</span>
          <button
            className={baseItemClass}
            onClick={() => handleChange(totalPages)}
          >
            {toFarsiNumber(totalPages)}
          </button>
        </>
      )}

      <button
        className="w-10 h-10 flex items-center justify-center rounded-md bg-white text-[#71717A] disabled:text-[#D4D4D8]"
        onClick={() => handleChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <AngleLeftIcon />
      </button>
    </div>
  );
};

export default Pagination;
